/**
 * Submission Tracking Utilities
 * Implements client-side mechanisms to prevent multiple form submissions by guest users
 *
 * Uses multiple strategies:
 * 1. Browser Fingerprinting - generates a unique hash based on browser characteristics
 * 2. Local Storage - stores submission history per browser
 * 3. Cookies - backup method for cross-tab tracking
 *
 * @since 1.0.1
 */

/**
 * Generate a browser fingerprint
 * Creates a unique identifier based on browser characteristics
 */
export function generateBrowserFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset().toString(),
    screen.colorDepth.toString(),
    screen.width.toString() + 'x' + screen.height.toString(),
    navigator.hardwareConcurrency?.toString() || '',
    navigator.platform,
  ];

  // Create a simple hash from the components
  const fingerprint = hashString(components.join('|'));
  return fingerprint;
}

/**
 * Simple string hashing function (FNV-1a algorithm)
 */
function hashString(str: string): string {
  let hash = 2166136261; // FNV offset basis

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }

  return (hash >>> 0).toString(16);
}

/**
 * Check if user has already submitted a form (client-side check)
 */
export function hasSubmittedForm(formId: number): boolean {
  // Check Local Storage
  const localStorageKey = `easyforms_submitted_${formId}`;
  if (localStorage.getItem(localStorageKey)) {
    return true;
  }

  // Check Session Storage (for same tab tracking)
  if (sessionStorage.getItem(localStorageKey)) {
    return true;
  }

  // Check Cookie
  return !!getCookie(localStorageKey);
}

/**
 * Mark form as submitted (client-side tracking)
 */
export function markFormAsSubmitted(formId: number): void {
  const localStorageKey = `easyforms_submitted_${formId}`;
  const timestamp = new Date().toISOString();

  // Store in Local Storage (persists across sessions)
  try {
    localStorage.setItem(localStorageKey, timestamp);
  } catch (e) {
    console.warn('Failed to set localStorage:', e);
  }

  // Store in Session Storage (same tab tracking)
  try {
    sessionStorage.setItem(localStorageKey, timestamp);
  } catch (e) {
    console.warn('Failed to set sessionStorage:', e);
  }

  // Store in Cookie (expires in 365 days)
  setCookie(localStorageKey, timestamp, 365);
}

/**
 * Clear submission tracking for a form (for testing or admin purposes)
 */
export function clearFormSubmissionTracking(formId: number): void {
  const localStorageKey = `easyforms_submitted_${formId}`;

  localStorage.removeItem(localStorageKey);
  sessionStorage.removeItem(localStorageKey);
  deleteCookie(localStorageKey);
}

/**
 * Get cookie value
 */
function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }

  return null;
}

/**
 * Set cookie
 */
function setCookie(name: string, value: string, days: number): void {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Strict";
}

/**
 * Delete cookie
 */
function deleteCookie(name: string): void {
  document.cookie = name + '=; Max-Age=-99999999; path=/';
}

/**
 * Get all submission tracking info for debugging
 */
export function getSubmissionTrackingInfo(formId: number): {
  fingerprint: string;
  hasSubmitted: boolean;
  localStorage: string | null;
  sessionStorage: string | null;
  cookie: string | null;
} {
  const localStorageKey = `easyforms_submitted_${formId}`;

  return {
    fingerprint: generateBrowserFingerprint(),
    hasSubmitted: hasSubmittedForm(formId),
    localStorage: localStorage.getItem(localStorageKey),
    sessionStorage: sessionStorage.getItem(localStorageKey),
    cookie: getCookie(localStorageKey),
  };
}


